import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { ChevronLeft, Plus } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import {
  useMediaManagerPresetTemplateGroupsQuery,
  useMediaManagerTemplateGroupsQuery,
} from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupCard from 'modules/manager/ui/components/media-manager-template-group-card';
import { PRESET_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { TemplateGroupGridSkeleton } from 'modules/manager/ui/components/skeletons';
import useCreateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-create-template-group-modal';
import useDeleteTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-delete-template-group-modal';
import useDuplicateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-group-modal';
import useRenameTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-rename-template-group-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDButton, UDText } from 'ud-ui/components';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';

const MediaManagerPresetsPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const { data: packs = [], currentData, isFetching, isError, refetch } = useMediaManagerPresetTemplateGroupsQuery();
  const packsState = deriveAsyncState({
    itemsCount: packs.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });
  const { data: orgGroups = [] } = useMediaManagerTemplateGroupsQuery(
    { tournamentId: tournamentId ?? 0 },
    { skip: !tournamentId },
  );

  const breadcrumbs = useMemo(
    () => [
      { label: t`Медиа менеджер`, to: Pages.MEDIA_MANAGER.INDEX },
      { label: t`Общие шаблоны`, to: Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY },
    ],
    [t],
  );

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const loadPacks = useCallback(() => {
    void refetch();
  }, [refetch]);

  const openGroup = useCallback(
    (group: MediaManagerTemplateGroup) => navigate(Pages.MEDIA_MANAGER.builders.group(group.id)),
    [navigate],
  );

  const onDuplicated = useCallback(
    (copy: MediaManagerTemplateGroup) => {
      navigate(Pages.MEDIA_MANAGER.builders.group(copy.id));
    },
    [navigate],
  );

  const existingPackNames = useMemo(() => packs.map((group) => group.name.trim()), [packs]);
  const existingOrgNames = useMemo(() => orgGroups.map((group) => group.name.trim()), [orgGroups]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateGroupModal({
    tournamentId: tournamentId ?? null,
    existingNames: existingPackNames,
    onCreated: loadPacks,
    createAsPreset: true,
  });
  const { modal: renameModal, open: openRenameModal } = useRenameTemplateGroupModal({
    groups: packs,
    onUpdated: loadPacks,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateGroupModal({
    onDeleted: loadPacks,
  });
  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateGroupModal({
    existingNames: existingOrgNames,
    onDuplicated,
  });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.INDEX)}>
          <ChevronLeft size={16} className="mr-2" />
          <Trans>К моим шаблонам</Trans>
        </UDButton>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
              <Plus size={16} className="mr-2" />
              <Trans>Создать группу</Trans>
            </UDButton>
          )}
        </div>
      </div>

      {packsState.status === 'loading' ? (
        <TemplateGroupGridSkeleton canWrite={isAdmin} canDuplicate={canDuplicate} />
      ) : packsState.status === 'error' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2" role="alert">
          <UDText variant="body-2">
            <Trans>Не удалось загрузить общие шаблоны</Trans>
          </UDText>
          <UDButton variant="secondary" onClick={loadPacks}>
            <Trans>Повторить</Trans>
          </UDButton>
        </div>
      ) : packsState.status === 'empty' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2">
          <UDText variant="body-2" className="text-gray-500">
            <Trans>Пока нет общих шаблонов</Trans>
          </UDText>
          {isAdmin && (
            <UDButton variant="primary" onClick={openCreateModal}>
              <Plus size={16} className="mr-2" /> <Trans>Создать группу</Trans>
            </UDButton>
          )}
        </div>
      ) : (
        <div
          className={classNames(PRESET_GRID_CLASS, 'transition-opacity', {
            'pointer-events-none opacity-50': packsState.isRefreshing,
          })}
          aria-busy={packsState.isRefreshing}
        >
          {packs.map((group) => (
            <MediaManagerTemplateGroupCard
              key={group.id}
              group={group}
              canWrite={isAdmin}
              canDuplicate={canDuplicate}
              isDuplicating={isDuplicating}
              onOpen={openGroup}
              onRename={openRenameModal}
              onDelete={openDeleteModal}
              onDuplicate={openDuplicateModal}
            />
          ))}
        </div>
      )}

      {createModal}
      {renameModal}
      {deleteModal}
      {duplicateModal}
    </div>
  );
};

export default MediaManagerPresetsPage;
